import React, { useState, useEffect } from 'react';
import { Box, Typography, Skeleton, IconButton, CircularProgress } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { ArrowLeftIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import api from '../../api/api';
import { axisTick, tooltipContentStyle } from './chartTheme';

interface CategoryData {
  category: string;
  categoryId?: number;
  amount: number;
  percent: number;
}

interface DrillExpense {
  id: number;
  description: string;
  amount: number;
  date: string;
  supplier?: string;
  supplierName?: string;
}

interface ExpenseCategoryPieProps {
  data: CategoryData[];
  loading?: boolean;
  /** "expense" or "income" — controls labels and drill-down API */
  label?: string;
  /** Date range for drill-down queries */
  dateFrom?: string;
  dateTo?: string;
}

// Warm, theme-aware palette
const PALETTE = [
  '#806464', '#A3B18A', '#D99982', '#5D6A4E', '#C98B5F',
  '#8E6E6E', '#7B9971', '#E8B298', '#6B7A5A', '#B3A394',
];

interface LabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  category: string;
}

const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, category }: LabelProps) => {
  if (percent < 5) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 1.35;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={axisTick.fill}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={axisTick.fontSize}
      fontWeight={500}
    >
      {category} ({Math.round(percent)}%)
    </text>
  );
};

const ExpenseCategoryPie: React.FC<ExpenseCategoryPieProps> = ({
  data,
  loading = false,
  label = 'expense',
  dateFrom,
  dateTo,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [drillCategory, setDrillCategory] = useState<CategoryData | null>(null);
  const [drillExpenses, setDrillExpenses] = useState<DrillExpense[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const gridColor = theme.components?.card?.border || '#E0E0E0';
  const textColor = theme.gray?.[500] || '#666';

  // Fetch expenses when drilling down
  useEffect(() => {
    if (!drillCategory) {
      setDrillExpenses([]);
      return;
    }

    const fetchDrillData = async () => {
      setDrillLoading(true);
      try {
        const isExpense = label === 'expense';
        const endpoint = isExpense
          ? '/api/vendor/finance/expenses'
          : '/api/vendor/finance/income';

        const params: Record<string, string> = {
          page: '0',
          size: '20',
        };
        if (drillCategory.categoryId) {
          params.categoryId = String(drillCategory.categoryId);
        } else {
          params.category = drillCategory.category;
        }
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;

        const response = await api.get(endpoint, { params });
        const items = response.data?.content || response.data?.expenses || response.data || [];
        const mapped: DrillExpense[] = (Array.isArray(items) ? items : []).map((e: Record<string, unknown>) => ({
          id: e.id as number,
          description: (e.description || e.source || '') as string,
          amount: (e.amount || 0) as number,
          date: (e.date || '') as string,
          supplier: (e.supplierName || e.supplier || e.counterparty || '') as string,
        }));
        setDrillExpenses(mapped);
      } catch (err) {
        console.error('Drill-down fetch failed:', err);
        setDrillExpenses([]);
      } finally {
        setDrillLoading(false);
      }
    };

    fetchDrillData();
  }, [drillCategory, label, dateFrom, dateTo]);

  if (loading) {
    return (
      <Box sx={{ width: '100%', height: 400 }}>
        <Skeleton
          variant="circular"
          width={240}
          height={240}
          sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0', mx: 'auto', mt: 6 }}
        />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '12px',
          border: `1px dashed ${gridColor}`,
        }}
      >
        <Typography variant="body2" sx={{ color: textColor }}>
          No {label} categories yet
        </Typography>
      </Box>
    );
  }

  // Drill-down view
  if (drillCategory) {
    const colorIndex = data.findIndex(d => d.category === drillCategory.category);
    const color = PALETTE[colorIndex % PALETTE.length];
    const isExpense = label === 'expense';
    const filterParams = new URLSearchParams();
    if (isExpense && drillCategory.categoryId) {
      filterParams.set('categoryId', String(drillCategory.categoryId));
    } else {
      filterParams.set('category', drillCategory.category);
    }
    if (dateFrom) filterParams.set('dateFrom', dateFrom);
    if (dateTo) filterParams.set('dateTo', dateTo);
    const filterPath = isExpense
      ? `/vendor/finance/expenses?${filterParams.toString()}`
      : `/vendor/finance/income?${filterParams.toString()}`;

    return (
      <Box sx={{ width: '100%', height: 400, display: 'flex', flexDirection: 'column' }}>
        {/* Drill-down header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <IconButton
            size="small"
            onClick={() => setDrillCategory(null)}
            className="nav-button"
            sx={{
              p: '4px',
              backgroundColor: `${theme.primary}15`,
              borderRadius: '6px',
              '&:hover': { backgroundColor: `${theme.primary}25` },
            }}
          >
            <ArrowLeftIcon style={{ width: 16, height: 16, color: theme.primary }} />
          </IconButton>
          <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: color, flexShrink: 0 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: theme.text }}>
            {drillCategory.category}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: textColor, ml: 'auto' }}>
            {formatCurrency(drillCategory.amount, { showDecimals: false })} ({Math.round(drillCategory.percent)}%)
          </Typography>
        </Box>

        {/* Expense list */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            borderRadius: '8px',
            border: `1px solid ${gridColor}`,
          }}
        >
          {drillLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={28} sx={{ color: theme.primary }} />
            </Box>
          ) : drillExpenses.length > 0 ? (
            drillExpenses.map((expense, idx) => (
              <Box
                key={expense.id || idx}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 1.5,
                  py: 1,
                  borderBottom: `1px solid ${gridColor}33`,
                  '&:hover': { backgroundColor: `${theme.primary}05` },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 500, color: theme.text }}>
                    {expense.supplier || expense.description}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: '0.7rem', color: textColor }}>
                    {expense.date}
                    {expense.description && expense.supplier ? ` — ${expense.description}` : ''}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: isExpense ? (theme.danger || '#D99982') : (theme.success || '#4CAF50'), flexShrink: 0, ml: 1 }}>
                  {formatCurrency(expense.amount, { showDecimals: true })}
                </Typography>
                {isExpense && expense.id && (
                  <IconButton
                    size="small"
                    className="nav-button"
                    onClick={() => {
                      const params = new URLSearchParams({ edit: String(expense.id) });
                      // Scope the list page to the same category + supplier + date range
                      // the user was looking at — avoids "list shows everything" surprise.
                      if (drillCategory.categoryId) {
                        params.set('categoryId', String(drillCategory.categoryId));
                      }
                      const sup = expense.supplier || expense.supplierName;
                      if (sup) params.set('supplierName', sup);
                      if (dateFrom) params.set('dateFrom', dateFrom);
                      if (dateTo) params.set('dateTo', dateTo);
                      navigate(`/vendor/finance/expenses?${params.toString()}`);
                    }}
                    sx={{ ml: 0.5, p: '3px', color: textColor, '&:hover': { color: theme.primary } }}
                    title="Edit / Recategorize"
                  >
                    <PencilSquareIcon style={{ width: 14, height: 14 }} />
                  </IconButton>
                )}
              </Box>
            ))
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.8rem', color: textColor }}>
                No individual entries found
              </Typography>
            </Box>
          )}
        </Box>

        {/* View all link */}
        <Box sx={{ mt: 1, textAlign: 'right' }}>
          <Typography
            component="span"
            onClick={() => navigate(filterPath)}
            sx={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: theme.primary,
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': { opacity: 0.8 },
            }}
          >
            View all {drillCategory.category} in {isExpense ? 'Expenses' : 'Income'} →
          </Typography>
        </Box>
      </Box>
    );
  }

  // Main pie chart view
  return (
    <Box sx={{ width: '100%', height: 400 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="amount"
            nameKey="category"
            cx="50%"
            cy="50%"
            outerRadius={120}
            innerRadius={55}
            paddingAngle={2}
            label={renderCustomLabel}
            labelLine={false}
            style={{ cursor: 'pointer' }}
            onClick={(_, index) => {
              if (data[index]) {
                setDrillCategory(data[index]);
              }
            }}
          >
            {data.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                fill={PALETTE[index % PALETTE.length]}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ ...tooltipContentStyle, color: theme.text }}
            formatter={(value: number, name: string) => [
              formatCurrency(value, { showDecimals: false }),
              name,
            ]}
          />
        </PieChart>
      </ResponsiveContainer>
      <Typography sx={{ textAlign: 'center', fontSize: '0.7rem', color: textColor, mt: -1 }}>
        Click a slice to drill down
      </Typography>
    </Box>
  );
};

export default ExpenseCategoryPie;
